'use client';

import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import TicketCard from './TicketCard';
import QuickEntrySheet from './QuickEntrySheet';
import { sameLondonDay } from '@/lib/homepage/format';

const CATEGORY_LABELS = {
  cash: 'Cash',
  experiences: 'Tickets & experiences',
  holidays: 'Holidays',
  products: 'Prizes',
};

const closeTime = (c) => new Date(c.endDate || c.drawDate).getTime();

const SORTS = {
  closing: { label: 'Closing soonest', fn: (a, b) => closeTime(a) - closeTime(b) },
  prize: { label: 'Biggest prize', fn: (a, b) => Number(b.prizeValue || 0) - Number(a.prizeValue || 0) },
  fewest: {
    label: 'Fewest tickets',
    fn: (a, b) => Number(a.totalTickets || 0) - Number(b.totalTickets || 0),
  },
  newest: {
    label: 'Newest',
    fn: (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
  },
};

/** Every live competition, with category tabs and a sort. */
export default function LiveGrid({ competitions, now }) {
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('closing');
  const [quick, setQuick] = useState(null);

  const tabs = useMemo(() => {
    const counts = {};
    competitions.forEach((c) => {
      const k = c.category || 'products';
      counts[k] = (counts[k] || 0) + 1;
    });
    const present = Object.keys(CATEGORY_LABELS).filter((k) => counts[k]);
    return [{ key: 'all', label: 'All', count: competitions.length }].concat(
      present.length > 1 ? present.map((k) => ({ key: k, label: CATEGORY_LABELS[k], count: counts[k] })) : []
    );
  }, [competitions]);

  const shown = useMemo(
    () =>
      competitions
        .filter((c) => category === 'all' || (c.category || 'products') === category)
        .sort(SORTS[sort].fn),
    [competitions, category, sort]
  );

  return (
    <>
      <div className="mt-8 flex flex-col-reverse gap-5 lg:flex-row lg:items-end lg:justify-between border-b border-white/[0.12]">
        <div role="tablist" aria-label="Filter by type" className="-mb-px flex gap-6 overflow-x-auto scrollbar-none pr-8 lg:pr-0 [mask-image:linear-gradient(90deg,#000_85%,transparent)] lg:[mask-image:none]">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={category === t.key}
              onClick={() => setCategory(t.key)}
              className={`shrink-0 min-h-11 pb-3 border-b-[3px] font-semibold transition-colors ${
                category === t.key
                  ? 'border-accent text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label} <span className="tabular-nums text-muted-foreground">{t.count}</span>
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 pb-3 text-sm font-semibold text-muted-foreground">
          Sort
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="min-h-11 bg-transparent text-foreground font-semibold border-0 border-b-2 border-white/30 py-2 pl-0 pr-7 focus:ring-0 focus:border-accent"
          >
            {Object.entries(SORTS).map(([k, s]) => (
              <option key={k} value={k} className="bg-card">
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
        {shown.map((c, i) => (
          <TicketCard key={c.id} c={c} now={now} priority={i < 4} onQuickEnter={setQuick} />
        ))}
      </div>

      {quick && (
        <QuickEntrySheet
          key={quick.id}
          competitions={[quick]}
          title="Quick enter"
          suggestions={competitions.filter(
            (o) => o.id !== quick.id && sameLondonDay(o.endDate || o.drawDate, quick.endDate || quick.drawDate)
          )}
          onSwitch={setQuick}
          onClose={() => setQuick(null)}
        />
      )}
    </>
  );
}

LiveGrid.propTypes = {
  competitions: PropTypes.array.isRequired,
  now: PropTypes.number,
};
